// Offline outbox: a small, persistent, per-entity coalescing queue of coach writes.
//
// Storage layout: ONE IndexedDB database/store (`sportschool-offline` / `outbox`, via idb-keyval) holding all
// entries, keyed `${userId}|${entryKey}` (e.g. `3f2…|att:<enrollmentId>`). A single store (instead of one store per
// `userId:schoolId`) lets the engine and the logout check enumerate everything without `indexedDB.databases()`
// (missing on older Safari). Every entry carries its own `schoolId` / `activeRole`, so school switches and reloads
// are safe; enrollment / session ids are globally unique, so the school is not needed in the key.
import { createStore, entries as idbEntries, set as idbSet, del as idbDel, type UseStore } from 'idb-keyval';
import {
  entryVersion,
  mergeAttendanceEntry,
  mergeSessionSummaryEntry,
  sessionSummaryKey,
  attendanceKey,
  storageKeyOf,
} from './core';
import type {
  AttendanceOutboxEntry,
  AttendanceWriteInput,
  OfflineIdentity,
  OutboxEntry,
  SessionSummaryOutboxEntry,
  SessionSummaryWriteInput,
} from './types';

/** Minimal persistence contract (IndexedDB in the app, in-memory in tests). */
export interface OutboxStorage {
  getAll(): Promise<OutboxEntry[]>;
  put(storageKey: string, entry: OutboxEntry): Promise<void>;
  delete(storageKey: string): Promise<void>;
}

export function createIdbOutboxStorage(dbName = 'sportschool-offline', storeName = 'outbox'): OutboxStorage {
  let store: UseStore | null = null;
  const getStore = () => (store ??= createStore(dbName, storeName));
  return {
    async getAll() {
      const rows = await idbEntries<string, OutboxEntry>(getStore());
      return rows.map(([, value]) => value).filter((v): v is OutboxEntry => !!v && typeof v === 'object' && 'key' in v);
    },
    put: (storageKey, entry) => idbSet(storageKey, entry, getStore()),
    delete: (storageKey) => idbDel(storageKey, getStore()),
  };
}

export function createMemoryOutboxStorage(initial: OutboxEntry[] = []): OutboxStorage & { rows: Map<string, OutboxEntry> } {
  const rows = new Map<string, OutboxEntry>(initial.map((e) => [storageKeyOf(e.userId, e.key), e]));
  return {
    rows,
    async getAll() {
      return [...rows.values()].map((e) => ({ ...e }));
    },
    async put(storageKey, entry) {
      rows.set(storageKey, { ...entry });
    },
    async delete(storageKey) {
      rows.delete(storageKey);
    },
  };
}

type Listener = (entries: OutboxEntry[]) => void;

/**
 * In-memory view of the outbox, persisted write-through to `OutboxStorage`.
 * Reads are synchronous (memory); writes update memory at once and are persisted in order.
 */
export class Outbox {
  private readonly map = new Map<string, OutboxEntry>();
  private readonly listeners = new Set<Listener>();
  private persistChain: Promise<void> = Promise.resolve();
  private loadPromise: Promise<void> | null = null;
  private snapshot: OutboxEntry[] = [];

  private readonly storage: OutboxStorage;
  private readonly now: () => number;

  constructor(storage: OutboxStorage, now: () => number = () => Date.now()) {
    this.storage = storage;
    this.now = now;
  }

  /** Loads persisted entries (once). Entries written before loading finished win over the stored ones. */
  load(): Promise<void> {
    if (!this.loadPromise) {
      this.loadPromise = this.storage
        .getAll()
        .then((stored) => {
          for (const entry of stored) {
            const sk = storageKeyOf(entry.userId, entry.key);
            if (!this.map.has(sk)) this.map.set(sk, entry);
          }
          this.emit();
        })
        .catch((error) => {
          console.warn('[offline] failed to load outbox', error);
        });
    }
    return this.loadPromise;
  }

  /** Waits until all writes so far are persisted. */
  flushed(): Promise<void> {
    return this.persistChain;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** All entries (all users), sorted by creation time. Stable reference until the next change. */
  all(): OutboxEntry[] {
    return this.snapshot;
  }

  forUser(userId: string): OutboxEntry[] {
    return this.snapshot.filter((e) => e.userId === userId);
  }

  get(userId: string, key: string): OutboxEntry | undefined {
    return this.map.get(storageKeyOf(userId, key));
  }

  hasPending(userId?: string | null): boolean {
    return this.snapshot.some((e) => e.state === 'pending' && (!userId || e.userId === userId));
  }

  writeAttendance(input: AttendanceWriteInput, identity: OfflineIdentity): AttendanceOutboxEntry {
    const existing = this.get(identity.userId, attendanceKey(input.enrollmentId));
    const entry = mergeAttendanceEntry(
      existing?.kind === 'attendance' ? existing : undefined,
      input,
      identity,
      this.now()
    );
    this.put(entry);
    return entry;
  }

  writeSessionSummary(input: SessionSummaryWriteInput, identity: OfflineIdentity): SessionSummaryOutboxEntry {
    const existing = this.get(identity.userId, sessionSummaryKey(input.sessionId));
    const entry = mergeSessionSummaryEntry(
      existing?.kind === 'sessionSummary' ? existing : undefined,
      input,
      identity,
      this.now()
    );
    this.put(entry);
    return entry;
  }

  /**
   * Updates an entry only if it was not rewritten since `sent` was read (same write version).
   * Returns false when a newer write exists — the newer write is then sent by a later flush.
   */
  patchIfUnchanged(sent: OutboxEntry, patch: Partial<Pick<OutboxEntry, 'state' | 'attempts' | 'nextAttemptAt' | 'errorCode' | 'lastError'>>): boolean {
    const current = this.get(sent.userId, sent.key);
    if (!current || entryVersion(current) !== entryVersion(sent)) return false;
    this.put({ ...current, ...patch } as OutboxEntry);
    return true;
  }

  /** Removes an entry only if it was not rewritten since `sent` was read. */
  removeIfUnchanged(sent: OutboxEntry): boolean {
    const current = this.get(sent.userId, sent.key);
    if (!current || entryVersion(current) !== entryVersion(sent)) return false;
    this.remove(sent.userId, sent.key);
    return true;
  }

  /** Puts an entry back to pending for an immediate retry. */
  retry(userId: string, key: string): OutboxEntry | undefined {
    const current = this.get(userId, key);
    if (!current) return undefined;
    const next = { ...current, state: 'pending', attempts: 0, nextAttemptAt: undefined, errorCode: undefined, lastError: undefined } as OutboxEntry;
    this.put(next);
    return next;
  }

  remove(userId: string, key: string): OutboxEntry | undefined {
    const sk = storageKeyOf(userId, key);
    const current = this.map.get(sk);
    if (!current) return undefined;
    this.map.delete(sk);
    this.persist(() => this.storage.delete(sk));
    this.emit();
    return current;
  }

  private put(entry: OutboxEntry) {
    const sk = storageKeyOf(entry.userId, entry.key);
    this.map.set(sk, entry);
    this.persist(() => this.storage.put(sk, entry));
    this.emit();
  }

  private persist(op: () => Promise<void>) {
    this.persistChain = this.persistChain.then(op).catch((error) => {
      console.warn('[offline] failed to persist outbox change', error);
    });
  }

  private emit() {
    this.snapshot = [...this.map.values()].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    for (const listener of this.listeners) listener(this.snapshot);
  }
}
